import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaCheck,
  FaClock,
  FaExclamationTriangle,
  FaFlag,
  FaTools,
} from 'react-icons/fa';

const inProgressItems = [
  {
    title: 'Documentation utilisateur',
    detail:
      'Remise à jour progressive après chaque passe fonctionnelle, avec une seule source dans public/docs/user.',
    status: 'En cours',
  },
];

const futurePriorities = [
  {
    priority: 'P1',
    title: 'Fiabiliser permissions et navigation',
    items: [
      'Univers, dossiers, items : lecture, édition, administration.',
      'Vérifier les items affichés dans la navigation selon les droits.',
      'S’assurer que les listes d’items respectent univers, dossiers et permissions.',
      'Messages plus explicites quand une action est bloquée.',
      'Cas limites : suppression, duplication, partage d’onglets.',
    ],
  },
  {
    priority: 'P1',
    title: 'Stabiliser les boards',
    items: [
      'Déplacements et redimensionnements fiables sur grille et tableau blanc.',
      'Règles claires entre déplacement de widget et drag/drop fonctionnel.',
      'Tests de non-régression sur les interactions critiques.',
    ],
  },
  {
    priority: 'P2',
    title: 'Boîte à idées utilisateurs',
    items: [
      'Créer une page dédiée aux demandes utilisateurs.',
      'Permettre de noter les idées, demandes et irritants.',
      'Prévoir un statut simple : à trier, retenu, en cours, fait.',
    ],
  },
  {
    priority: 'P2',
    title: 'Améliorer les templates et l’onboarding',
    items: [
      'Créer un univers, un dossier, un premier item depuis des templates.',
      'Proposer des templates de départ.',
      'Prévoir une gestion avancée des versions de templates.',
      'Réduire les champs techniques visibles au premier contact.',
    ],
  },
  {
    priority: 'P2',
    title: 'Renforcer les widgets avancés',
    items: [
      'Filtres : cas limites drag/drop, verrouillage, affichages longs.',
      'Timeline : édition visuelle, valeurs calculées, modes verticaux.',
      'Générateur : ergonomie, import/export, documentation d’exemples.',
    ],
  },
  {
    priority: 'P3',
    title: 'Connexion Discord',
    items: [
      'Ajouter la connexion par Discord.',
      'Lier un compte Discord à un compte existant.',
      'Préparer les cas de conflit entre comptes et identités.',
    ],
  },
  {
    priority: 'P3',
    title: 'Préparer une beta privée',
    items: [
      'Changelog utilisable.',
      'Liste courte des fonctionnalités expérimentales.',
      'Canal clair pour remonter bugs et retours.',
    ],
  },
];

const recentlyDone = [
  'Templates V1 : création depuis template avec clonage des templates liés et conservation des références standards.',
  'Centralisation des permissions : liens, navigation et drawer respectent les dossiers privés et les droits de lecture.',
  'Stabilisation des images : fallback dossier → univers → logo application, preview exploitable, URL i.ibb.co sans re-upload.',
];

const priorityClassName = priority => {
  if (priority === 'P1') {
    return 'border-red text-red';
  }
  if (priority === 'P2') {
    return 'border-secondary text-secondary';
  }
  return 'border-primary text-primary-l';
};

function Roadmap() {
  return (
    <div className='min-h-screen bg-primary-d text-contrast overflow-auto'>
      <div className='max-w-6xl mx-auto px-1 py-2 md:px-2 md:py-3'>
        <div className='flex flex-wrap items-center justify-between gap-1 mb-2'>
          <div>
            <div className='text-xs uppercase tracking-widest text-secondary font-condensed'>
              Alpha interne
            </div>
            <h1 className='font-condensed uppercase text-4xl md:text-5xl font-bold'>
              Roadmap
            </h1>
          </div>
          <Link
            to='/'
            className='rounded bg-main bg-opacity-40 border border-primary border-opacity-40 px-1 py-05 text-sm hover:bg-opacity-70 transition'
          >
            Accueil
          </Link>
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-roadmap gap-1 mb-1'>
          <section className='rounded-lg bg-main bg-opacity-35 border border-secondary border-opacity-40 p-1 md:p-2'>
            <div className='flex items-center gap-05 mb-1'>
              <FaTools className='text-secondary' />
              <h2 className='font-condensed uppercase text-2xl font-bold'>
                En cours de développement
              </h2>
            </div>
            <div className='relative pl-1'>
              <div className='absolute left-02 top-05 bottom-05 w-px bg-secondary bg-opacity-50' />
              <div className='flex flex-col gap-1'>
                {inProgressItems.map(item => (
                  <div
                    key={item.title}
                    className='relative pl-1 rounded bg-primary-d bg-opacity-40 border border-primary border-opacity-30 p-1'
                  >
                    <div className='absolute -left-dot top-1 rounded-full bg-secondary w-05 h-05' />
                    <div className='flex flex-wrap items-center justify-between gap-05 mb-03'>
                      <h3 className='font-condensed uppercase text-lg font-bold'>
                        {item.title}
                      </h3>
                      <span className='inline-flex items-center gap-03 rounded-full border border-secondary border-opacity-50 px-05 py-01 text-xs text-secondary'>
                        <FaClock />
                        {item.status}
                      </span>
                    </div>
                    <p className='text-sm text-contrast text-opacity-75'>
                      {item.detail}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <aside className='rounded-lg bg-main bg-opacity-35 border border-green border-opacity-40 p-1 md:p-2'>
            <div className='flex items-center gap-05 mb-1'>
              <FaCheck className='text-green' />
              <h2 className='font-condensed uppercase text-2xl font-bold'>
                Derniers faits
              </h2>
            </div>
            <ul className='flex flex-col gap-05'>
              {recentlyDone.map(item => (
                <li
                  key={item}
                  className='flex gap-05 text-sm text-contrast text-opacity-85'
                >
                  <FaCheck className='text-green mt-01 flex-none' />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </aside>
        </div>

        <section className='rounded-lg bg-main bg-opacity-35 border border-primary border-opacity-40 p-1 md:p-2'>
          <div className='flex items-center gap-05 mb-1'>
            <FaFlag className='text-primary-l' />
            <h2 className='font-condensed uppercase text-2xl font-bold'>
              Futurs développements
            </h2>
          </div>
          <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-1'>
            {futurePriorities.map(block => (
              <div
                key={`${block.priority}-${block.title}`}
                className={`rounded bg-primary-d bg-opacity-35 border p-1 ${priorityClassName(
                  block.priority,
                )}`}
              >
                <div className='flex items-center gap-05 mb-05'>
                  <span className='font-condensed text-xl font-bold'>
                    {block.priority}
                  </span>
                  <h3 className='font-condensed uppercase text-lg font-bold text-contrast'>
                    {block.title}
                  </h3>
                </div>
                <ul className='flex flex-col gap-04'>
                  {block.items.map(item => (
                    <li
                      key={item}
                      className='flex gap-05 text-sm text-contrast text-opacity-80'
                    >
                      <FaExclamationTriangle className='mt-01 flex-none text-current' />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default Roadmap;
